import { useState, useRef, useCallback } from "react";
import { client } from "../services/axiosClient";

export const FILTER_SEARCH_MIN_CHARS = 3;
export const FILTER_SEARCH_DEBOUNCE_MS = 350;

/**
 * Busca de itens de filtro com debounce, cancelamento (AbortController) e
 * mínimo de caracteres.
 *
 * @param {object} opts
 * @param {string} [opts.searchEndpoint]  Endpoint que aceita POST com { filter, q }
 *                                        e retorna { items: string[] }.
 *                                        Quando null/undefined, só a filtragem local ocorre.
 *
 * Retorna `null` para `searchResults` enquanto a query não atingir o mínimo
 * de caracteres ou enquanto não houver endpoint — neste caso, usar a lista
 * pré-carregada do meta para filtragem local.
 */
export function useFilterItemSearch({ searchEndpoint } = {}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback(
    (query, filterKey) => {
      setSearchQuery(query);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      if (!query || query.length < FILTER_SEARCH_MIN_CHARS || !searchEndpoint) {
        setSearchResults(null);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          const response = await client.post(
            searchEndpoint,
            { filter: filterKey, q: query },
            { signal: controller.signal },
          );
          if (!controller.signal.aborted) {
            setSearchResults(response.data.items || []);
            setIsSearching(false);
          }
        } catch (e) {
          if (!controller.signal.aborted) {
            setSearchResults([]);
            setIsSearching(false);
          }
        }
      }, FILTER_SEARCH_DEBOUNCE_MS);
    },
    [searchEndpoint],
  );

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setSearchQuery("");
    setSearchResults(null);
    setIsSearching(false);
  }, []);

  return { searchQuery, searchResults, isSearching, search, reset };
}
